import { APP_VERSION, CODE_ENV, CURRENT_CODE_ENV } from "./constant";
import { getMtAppVersion, getPlatform, getRuntimeEnv } from "./getBrowserEnv";
import { getUnionId } from "./userInfo";

export enum Level {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
}

/**
 * 检查 Owl 是否已就绪
 */
function isOwlReady(): boolean {
  if (typeof window === 'undefined') return false;
  return (window as any).__OWL_READY__ === true;
}

/**
 * 标记 Owl 已就绪，用于水合完成后调用
 */
export function setOwlReady() {
  if (typeof window !== 'undefined') {
    (window as any).__OWL_READY__ = true;
  }
}

/**
 * 上报自定义指标
 * 在 Owl 未就绪时会存储到队列，等待 flushCustomMetricsQueue 统一上报
 */
export function reportCustomMetric({
  key,
  value,
  tags = {},
  msg,
}: {
  key: string;
  value: number;
  tags?: Record<string, string | number | boolean>;
  msg?: string;
}) {
  // 如果 Owl 未就绪，先存储到队列
  if (!isOwlReady()) {
    if (typeof window !== 'undefined') {
      if (!(window as any).__CUSTOM_METRICS_QUEUE__) {
        (window as any).__CUSTOM_METRICS_QUEUE__ = [];
      }
      (window as any).__CUSTOM_METRICS_QUEUE__.push({ key, value, tags, msg });
    }
    return;
  }

  // Owl 已就绪，直接上报
  const metricManager = owl('newMetricInst');
  const mergedTags = {
    platform: getPlatform(),
    container: getRuntimeEnv(),
    pagePath: location?.pathname,
    ...tags,
  };
  metricManager?.setMetric(key, value, mergedTags);
  if (msg) {
    metricManager?.setExtraData(msg);
  }
  metricManager?.report();
}

/**
 * 批量上报队列中的自定义指标
 * 在 useEffect 中调用，一次性上报所有存储的指标，减少请求数量
 */
export function flushCustomMetricsQueue() {
  if (typeof window === 'undefined') return;

  const queue = (window as any).__CUSTOM_METRICS_QUEUE__;
  if (!queue || queue.length === 0) return;

  const metricManager = owl('newMetricInst');
  if (!metricManager) return;

  // 批量设置所有指标，最后统一上报
  queue.forEach(({ key, value, tags, msg }: {
    key: string;
    value: number;
    tags?: Record<string, string | number | boolean>;
    msg?: string;
  }) => {
    const mergedTags = {
      platform: getPlatform(),
      container: getRuntimeEnv(),
      pagePath: location?.pathname,
      ...tags,
    };
    metricManager.setMetric(key, value, mergedTags);
    if (msg) {
      metricManager.setExtraData(msg);
    }
  });

  // 一次性上报
  metricManager.report();

  // 清空队列
  (window as any).__CUSTOM_METRICS_QUEUE__ = [];
}

/**
 * 上报异常
 * @param name 错误名称
 * @param err 错误对象
 * @param opts 错误选项
 * @param context 上下文信息
 */
export function reportError({
  name,
  msg,
  category = 'customError',
  level = Level.WARN,
  tags,
}: {
  name: string;
  msg?: any;
  category?: 'customError' | 'jsError' | 'resourceError' | 'ajaxError' | 'bridgeError' | 'baseError' | 'supplyError' | 'couponError' | 'limitedTimeGrabError';
  level?: Level;
  tags?: Record<string, string | number>;
}) {
  const message = msg instanceof Error
    ? JSON.stringify(msg, Object.getOwnPropertyNames(msg))
    : JSON.stringify(msg);

  const mergedTags = {
    platform: getPlatform(),
    container: getRuntimeEnv(),
    version: APP_VERSION,
    mtAppVersion: getMtAppVersion(),
    unionId: getUnionId(),
    ssrTraceId: window?.__TRACE_ID__ || "",
    ...tags,
  };

  owl(
    'addError',
    {
      name,
      msg: message
    },
    {
      category,
      level,
      tags: mergedTags,
    },
  );
  if (CURRENT_CODE_ENV !== CODE_ENV.PRODUCTION) {
    console.error(name, message);
  }
}

export function setOwlTag(tags: Record<string, string | number>) {
  owl('setTag', tags);
}

export function reportPerfMetric({
  key,
  value,
  tags = {},
}: {
  key: string;
  value: number;
  tags?: Record<string, string | number | boolean>;
}) {
  owl('addCustomMetrics', key, value, tags)
}
